/**
 * FormattingCommands - High-level formatting operations for toolbar/keyboard
 *
 * This module provides the command layer for MS Word-style text formatting:
 * - Apply formatting to selected text (bold, italic, font change, etc.)
 * - Toggle formatting (Ctrl+B, Ctrl+I)
 * - Set font family/size for selection or new text
 *
 * These commands work with the FormattingSpanManager to manipulate spans
 * and the DocumentOrchestrator to trigger re-layout.
 *
 * @module FormattingCommands
 * @since 2026-01-21
 */

import type {
  FormattingSpan,
  FormattingAttributes,
  ResolvedFormatting
} from '../../../core/types/FormattingSpan';
import { DEFAULT_FORMATTING } from '../../../core/types/FormattingSpan';
import { FormattingSpanManager } from '../../../core/services/FormattingSpanManager';
import type { Paragraph } from '../EdgeDocsTypes';
import type { EditorSelection } from '../../composer/EditorContracts';
import { WasmLogger } from '@/utils/loggers/WasmLogger';

// ============================================================================
// Types
// ============================================================================

/**
 * Document state required for formatting operations
 */
export interface FormattingDocumentState {
  text: string;
  paragraphs: Paragraph[];
}

/**
 * Result of a formatting operation
 */
export interface FormattingResult {
  /** Updated paragraphs (only affected paragraphs) */
  updatedParagraphs: Paragraph[];
  /** Paragraph IDs that were modified (for cache invalidation) */
  affectedParagraphIds: string[];
}

/**
 * Selection range relative to a specific paragraph
 */
interface ParagraphSelectionRange {
  paragraphId: string;
  paragraphIndex: number;
  /** Start grapheme index relative to paragraph (inclusive) */
  localStart: number;
  /** End grapheme index relative to paragraph (exclusive) */
  localEnd: number;
}

// ============================================================================
// Selection Utilities
// ============================================================================

/**
 * Convert a document-level selection to paragraph-relative ranges
 *
 * A selection may span multiple paragraphs. This function returns
 * an array of ranges, one per affected paragraph, with positions
 * relative to each paragraph's start.
 *
 * @param selection - Editor selection with anchor and focus
 * @param documentState - Document state with paragraphs
 * @returns Array of paragraph-relative selection ranges
 */
export function selectionToParagraphRanges(
  selection: EditorSelection,
  documentState: FormattingDocumentState
): ParagraphSelectionRange[] {
  if (!selection || selection.isCollapsed) {
    return [];
  }

  const { paragraphs } = documentState;
  if (!paragraphs || paragraphs.length === 0) {
    return [];
  }

  // Get anchor and focus positions
  // Use logical.charOffset if available, otherwise calculate from x/y
  const anchorPos = selection.anchor.logical?.charOffset ?? 0;
  const focusPos = selection.focus.logical?.charOffset ?? 0;

  // Normalize to start/end
  const selStart = Math.min(anchorPos, focusPos);
  const selEnd = Math.max(anchorPos, focusPos);

  if (selStart >= selEnd) {
    return [];
  }

  const ranges: ParagraphSelectionRange[] = [];

  for (let i = 0; i < paragraphs.length; i++) {
    const para = paragraphs[i];
    const paraStart = para.textStart;
    const paraEnd = para.textEnd;

    // Check if selection overlaps with this paragraph
    if (selEnd <= paraStart || selStart >= paraEnd) {
      continue; // No overlap
    }

    // Calculate overlap
    const overlapStart = Math.max(selStart, paraStart);
    const overlapEnd = Math.min(selEnd, paraEnd);

    // Convert to paragraph-relative indices
    const localStart = overlapStart - paraStart;
    const localEnd = overlapEnd - paraStart;

    if (localStart < localEnd) {
      ranges.push({
        paragraphId: para.id,
        paragraphIndex: i,
        localStart,
        localEnd
      });
    }
  }

  WasmLogger.formatting?.selectionToRanges?.({
    selectionStart: selStart,
    selectionEnd: selEnd,
    paragraphsAffected: ranges.length
  });

  return ranges;
}

// ============================================================================
// Formatting Commands
// ============================================================================

/**
 * Apply formatting to selected text
 *
 * This is the primary formatting function used by toolbar actions.
 * It handles multi-paragraph selections and updates formatting spans.
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @param formatting - Formatting attributes to apply
 * @returns Updated paragraphs and affected IDs
 */
export function applyFormattingToSelection(
  documentState: FormattingDocumentState,
  selection: EditorSelection,
  formatting: FormattingAttributes
): FormattingResult {
  const ranges = selectionToParagraphRanges(selection, documentState);

  if (ranges.length === 0) {
    WasmLogger.formatting?.applyFormatting?.({
      skipped: true,
      reason: 'no selection or empty selection'
    });
    return { updatedParagraphs: [], affectedParagraphIds: [] };
  }

  WasmLogger.formatting?.applyFormatting?.({
    ranges: ranges.length,
    attrs: Object.keys(formatting)
  });

  const updatedParagraphs: Paragraph[] = [];
  const affectedParagraphIds: string[] = [];

  for (const range of ranges) {
    const para = documentState.paragraphs[range.paragraphIndex];

    // Get existing spans or empty array
    const existingSpans: FormattingSpan[] = para.formattingSpans ?? [];

    // Apply formatting to range
    const newSpans = FormattingSpanManager.applyFormatting(
      existingSpans,
      range.localStart,
      range.localEnd,
      formatting
    );

    // Create updated paragraph with new spans and incremented version
    const updatedPara: Paragraph = {
      ...para,
      formattingSpans: newSpans,
      version: para.version + 1
    };

    updatedParagraphs.push(updatedPara);
    affectedParagraphIds.push(para.id);
  }

  return { updatedParagraphs, affectedParagraphIds };
}

/**
 * Toggle bold formatting for selected text
 *
 * If all selected text is bold, removes bold. Otherwise, applies bold.
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @returns Updated paragraphs and affected IDs
 */
export function toggleBold(
  documentState: FormattingDocumentState,
  selection: EditorSelection
): FormattingResult {
  const ranges = selectionToParagraphRanges(selection, documentState);

  if (ranges.length === 0) {
    return { updatedParagraphs: [], affectedParagraphIds: [] };
  }

  // Check if selection is already bold
  const isCurrentlyBold = isSelectionBold(documentState, ranges);

  WasmLogger.formatting?.toggleBold?.({
    isCurrentlyBold,
    willApply: !isCurrentlyBold
  });

  if (isCurrentlyBold) {
    // Remove bold
    return removeFormattingFromSelection(documentState, selection, ['fontWeight']);
  } else {
    // Apply bold
    return applyFormattingToSelection(documentState, selection, { fontWeight: 700 });
  }
}

/**
 * Toggle italic formatting for selected text
 *
 * If all selected text is italic, removes italic. Otherwise, applies italic.
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @returns Updated paragraphs and affected IDs
 */
export function toggleItalic(
  documentState: FormattingDocumentState,
  selection: EditorSelection
): FormattingResult {
  const ranges = selectionToParagraphRanges(selection, documentState);

  if (ranges.length === 0) {
    return { updatedParagraphs: [], affectedParagraphIds: [] };
  }

  // Check if selection is already italic
  const isCurrentlyItalic = isSelectionItalic(documentState, ranges);

  WasmLogger.formatting?.toggleItalic?.({
    isCurrentlyItalic,
    willApply: !isCurrentlyItalic
  });

  if (isCurrentlyItalic) {
    // Remove italic
    return removeFormattingFromSelection(documentState, selection, ['fontStyle']);
  } else {
    // Apply italic
    return applyFormattingToSelection(documentState, selection, { fontStyle: 'italic' });
  }
}

/**
 * Set font family for selected text
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @param fontFamily - Font UID (e.g., "Inter-Bold.ttf")
 * @returns Updated paragraphs and affected IDs
 */
export function setFontFamily(
  documentState: FormattingDocumentState,
  selection: EditorSelection,
  fontFamily: string
): FormattingResult {
  WasmLogger.formatting?.setFontFamily?.({ fontFamily });
  return applyFormattingToSelection(documentState, selection, { fontFamily });
}

/**
 * Set font size for selected text
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @param fontSizePx - Font size in pixels
 * @returns Updated paragraphs and affected IDs
 */
export function setFontSize(
  documentState: FormattingDocumentState,
  selection: EditorSelection,
  fontSizePx: number
): FormattingResult {
  WasmLogger.formatting?.setFontSize?.({ fontSizePx });
  return applyFormattingToSelection(documentState, selection, { fontSizePx });
}

/**
 * Set font weight for selected text
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @param fontWeight - Font weight (100-900)
 * @returns Updated paragraphs and affected IDs
 */
export function setFontWeight(
  documentState: FormattingDocumentState,
  selection: EditorSelection,
  fontWeight: number
): FormattingResult {
  return applyFormattingToSelection(documentState, selection, { fontWeight });
}

/**
 * Remove specific formatting properties from selected text
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @param properties - Properties to remove (e.g., ['fontWeight', 'fontStyle'])
 * @returns Updated paragraphs and affected IDs
 */
export function removeFormattingFromSelection(
  documentState: FormattingDocumentState,
  selection: EditorSelection,
  properties: (keyof FormattingAttributes)[]
): FormattingResult {
  const ranges = selectionToParagraphRanges(selection, documentState);

  if (ranges.length === 0) {
    return { updatedParagraphs: [], affectedParagraphIds: [] };
  }

  WasmLogger.formatting?.removeFormatting?.({
    ranges: ranges.length,
    properties
  });

  const updatedParagraphs: Paragraph[] = [];
  const affectedParagraphIds: string[] = [];

  for (const range of ranges) {
    const para = documentState.paragraphs[range.paragraphIndex];
    const existingSpans: FormattingSpan[] = para.formattingSpans ?? [];

    const newSpans = FormattingSpanManager.removeFormatting(
      existingSpans,
      range.localStart,
      range.localEnd,
      properties
    );

    const updatedPara: Paragraph = {
      ...para,
      formattingSpans: newSpans.length > 0 ? newSpans : undefined,
      version: para.version + 1
    };

    updatedParagraphs.push(updatedPara);
    affectedParagraphIds.push(para.id);
  }

  return { updatedParagraphs, affectedParagraphIds };
}

/**
 * Clear all formatting from selected text (reset to defaults)
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @returns Updated paragraphs and affected IDs
 */
export function clearFormattingFromSelection(
  documentState: FormattingDocumentState,
  selection: EditorSelection
): FormattingResult {
  const ranges = selectionToParagraphRanges(selection, documentState);

  if (ranges.length === 0) {
    return { updatedParagraphs: [], affectedParagraphIds: [] };
  }

  WasmLogger.formatting?.clearFormatting?.({ ranges: ranges.length });

  const updatedParagraphs: Paragraph[] = [];
  const affectedParagraphIds: string[] = [];

  for (const range of ranges) {
    const para = documentState.paragraphs[range.paragraphIndex];
    const existingSpans: FormattingSpan[] = para.formattingSpans ?? [];

    const newSpans = FormattingSpanManager.clearFormatting(
      existingSpans,
      range.localStart,
      range.localEnd
    );

    const updatedPara: Paragraph = {
      ...para,
      formattingSpans: newSpans.length > 0 ? newSpans : undefined,
      version: para.version + 1
    };

    updatedParagraphs.push(updatedPara);
    affectedParagraphIds.push(para.id);
  }

  return { updatedParagraphs, affectedParagraphIds };
}

// ============================================================================
// Formatting Query Utilities
// ============================================================================

/**
 * Check if entire selection is bold
 */
function isSelectionBold(
  documentState: FormattingDocumentState,
  ranges: ParagraphSelectionRange[]
): boolean {
  for (const range of ranges) {
    const para = documentState.paragraphs[range.paragraphIndex];
    const spans: FormattingSpan[] = para.formattingSpans ?? [];

    // Check each position in range
    for (let pos = range.localStart; pos < range.localEnd; pos++) {
      const resolvedWeight = FormattingSpanManager.resolveFormattingAt(
        spans,
        pos,
        { fontWeight: para.fontWeight },
        DEFAULT_FORMATTING
      ).fontWeight;

      if (resolvedWeight < 700) {
        return false; // Found non-bold character
      }
    }
  }

  return true; // All characters are bold
}

/**
 * Check if entire selection is italic
 */
function isSelectionItalic(
  documentState: FormattingDocumentState,
  ranges: ParagraphSelectionRange[]
): boolean {
  for (const range of ranges) {
    const para = documentState.paragraphs[range.paragraphIndex];
    const spans: FormattingSpan[] = para.formattingSpans ?? [];

    // Check each position in range
    for (let pos = range.localStart; pos < range.localEnd; pos++) {
      const resolvedStyle = FormattingSpanManager.resolveFormattingAt(
        spans,
        pos,
        { fontStyle: para.fontStyle as 'normal' | 'italic' | 'oblique' },
        DEFAULT_FORMATTING
      ).fontStyle;

      if (resolvedStyle !== 'italic') {
        return false; // Found non-italic character
      }
    }
  }

  return true; // All characters are italic
}

/**
 * Get the current formatting at the selection start (for toolbar state)
 *
 * For collapsed selections (cursor with no selection), this function also checks
 * if the cursor is at the END of a formatting span. If so, it returns the span's
 * formatting. This matches MS Word behavior where cursor after bold text shows
 * "bold" in the toolbar, and pressing Ctrl+B disables bold for subsequent typing.
 *
 * @param documentState - Current document state
 * @param selection - Current editor selection
 * @returns Resolved formatting at selection start, or undefined if no selection
 */
export function getFormattingAtSelection(
  documentState: FormattingDocumentState,
  selection: EditorSelection
): ResolvedFormatting | undefined {
  if (!selection) {
    return undefined;
  }

  const { paragraphs } = documentState;
  if (!paragraphs || paragraphs.length === 0) {
    return undefined;
  }

  // Get the start position (anchor or focus, whichever is earlier)
  const anchorPos = selection.anchor.logical?.charOffset ?? 0;
  const focusPos = selection.focus.logical?.charOffset ?? 0;
  const pos = Math.min(anchorPos, focusPos);

  // Find paragraph containing position
  const paraIndex = paragraphs.findIndex(
    p => pos >= p.textStart && pos < p.textEnd
  );

  if (paraIndex === -1) {
    return undefined;
  }

  const para = paragraphs[paraIndex];
  const localPos = pos - para.textStart;
  const spans: FormattingSpan[] = para.formattingSpans ?? [];

  // Build paragraph formatting from paragraph fields
  const paraFormatting: Partial<ResolvedFormatting> = {};
  if (para.fontFamily) paraFormatting.fontFamily = para.fontFamily;
  if (para.fontSizePx) paraFormatting.fontSizePx = para.fontSizePx;
  if (para.fontWeight) paraFormatting.fontWeight = para.fontWeight;
  if (para.fontStyle) paraFormatting.fontStyle = para.fontStyle as 'normal' | 'italic' | 'oblique';

  // 🔧 FIX (2026-01-22): MS Word behavior for cursor at end of formatted span
  // When cursor is collapsed (no selection) and positioned at the END of a formatting span,
  // we should report that span's formatting. This allows the user to:
  // 1. See "Bold" highlighted in toolbar when cursor is after bold text
  // 2. Press Ctrl+B to DISABLE bold for subsequent typing (toggle logic works correctly)
  //
  // Without this fix, cursor at position 5 with span {start: 0, end: 5} is NOT detected
  // as "in bold" because resolveFormattingAt uses s.end > position (exclusive end).
  if (selection.isCollapsed && localPos > 0) {
    // Check if cursor is at the end of any formatting span
    const spanEndingHere = spans.find(s => s.end === localPos);
    if (spanEndingHere) {
      // Cursor is at the end of a span - use formatting from position before cursor
      // This matches MS Word: cursor after bold word = bold mode for toolbar
      return FormattingSpanManager.resolveFormattingAt(
        spans,
        localPos - 1,  // Look at character just before cursor
        paraFormatting,
        DEFAULT_FORMATTING
      );
    }
  }

  return FormattingSpanManager.resolveFormattingAt(spans, localPos, paraFormatting, DEFAULT_FORMATTING);
}
